/**
 * @format
 */
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EntityCollectionServiceBase, EntityCollectionServiceElementsFactory } from "@ngrx/data";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { Observable } from "rxjs/internal/Observable";
import { catchError, map } from "rxjs/operators";
import { Template } from "../../core/model/central/template.model";
import { previewEditTemplateEntityMetadata } from "./preview-edit-template.entity";
import {
   PreviewEditTemplate,
   PreviewTemplateContentResponse,
   TemplateParameterItemType,
   TemplateParameterResponse,
} from "./preview-edit-template.model";

@Injectable()
export class PreviewEditTemplateService extends EntityCollectionServiceBase<Template> {
   constructor(private serviceElementsFactory: EntityCollectionServiceElementsFactory, private httpClient: HttpClient) {
      super(previewEditTemplateEntityMetadata.entityName, serviceElementsFactory);
   }

   getModelContentByTemplateId(
      templateId: number,
      templateFileRef: string,
      updatedModel: any
   ): Observable<PreviewEditTemplate> {
      return this.httpClient
         .post(`servlets/templates/${templateId}/modelcontent/`, {
            templateFileRef,
            updatedModel,
         })
         .pipe(map((response: any) => this.cleanTemplateDefaultContent(response)));
   }

   loadTemplateSource(templateFilename): Observable<any> {
      return this.httpClient.get(`/servlets/templates/download/?fileName=${templateFilename}`, {
         observe: "body",
         responseType: "text",
      });
   }

   preprocessModel(mediaType, template, templateId, selectedUserId): Observable<any> {
      const model = {
         template,
         templateId,
         selectedUserId,
      };

      return this.httpClient.post(`/servlets/templates/preprocess/${mediaType}/`, { model });
   }

   getTemplateParameters(userId: number, type: string): Observable<Array<TemplateParameterItemType>> {
      return this.httpClient
         .get<TemplateParameterResponse>(
            `/servlets/templates/templateparameters/?selectedUserId=${userId}&type=${type}`,
            {
               observe: "body",
            }
         )
         .pipe(map((response) => response.templateParameters));
   }

   saveSource(template, templateId): Observable<any> {
      return this.httpClient
         .post("/servlets/templates/savetemplatesource/", { template, templateId })
         .pipe(map((response: any) => response));
   }

   validateSource(template, userName, userId, fileName): Observable<any> {
      return this.httpClient.post("/servlets/templates/validatesource/", {
         template,
         userName,
         userId,
         fileName,
      });
   }

   saveTemplate(templateId, isConfirmed, forRevert, template, userId, lastModifiedR): Observable<any> {
      let url = `/servlets/templates/${templateId}/`;
      const force = isConfirmed !== undefined && isConfirmed ? "?force=true" : "";
      url += force;

      const dataToRequest = this.findEditedTemplateFields(template, forRevert, undefined);
      console.log("dataToRequest", dataToRequest, typeof template, typeof forRevert);

      const lastModified = lastModifiedR;
      const previous = this.prepareTemplateDataToSending(forRevert);

      return this.httpClient
         .put(url, { lastModified, userId, previous, data: dataToRequest })
         .pipe(map((response: any) => response));
   }

   getCountOfStrategiesMappedWithTemplate(templateId, selectedUserName): Observable<any> {
      return this.httpClient
         .post("/servlets/templates/strategiesmapped/", {
            templateId,
            selectedUserName,
         })
         .pipe(map((response: any) => response));
   }

   prepareTemplateDataToSending(template): any {
      const tmp: any = { ...template };
      delete tmp.editingTemplate;
      delete tmp.isEdited;
      delete tmp.sending;

      return tmp;
   }

   findEditedTemplateFields(newTemplate, oldTemplate, isNeedParse): any {
      const modfyedFields = {};

      for (const i in newTemplate) {
         if (
            i === "sending" ||
            i === "editingTemplate" ||
            i === "isEdited" ||
            i === "createdDate" ||
            i === "lastModified"
         ) {
            continue;
         }
         if (oldTemplate[i] !== newTemplate[i]) {
            if (!isNeedParse || i !== "templateDefaultContent") {
               modfyedFields[i] = newTemplate[i];
               continue;
            }

            const templateDefaultContentNew = JSON.parse(newTemplate[i]);
            const templateDefaultContentOld = JSON.parse(oldTemplate[i]);

            for (const x in templateDefaultContentNew) {
               if (templateDefaultContentOld[x] !== templateDefaultContentNew[x]) {
                  modfyedFields[`template.${x}`] = templateDefaultContentNew[x];
               }
            }
         }
      }

      return modfyedFields;
   }

   processPreviewTemplate(
      templateId: number,
      userId: number,
      model: any,
      templateFilename: string,
      isConfirm: boolean
   ): Observable<PreviewTemplateContentResponse> {
      const templateUrl = "/servlets/templates/processpreviewtemplate/";
      const data = {
         selectedUserId: userId,
         templateName: templateFilename,
         isConfirm,
         model,
         templateId,
      };

      return this.httpClient.post<PreviewTemplateContentResponse>(templateUrl, { data });
   }

   saveAsCustomTemplate(template, userId, templateDefaultContent, source, saveType): Observable<any> {
      return this.httpClient.post("/servlets/templates/cloneascustom/", {
         template,
         userId,
         templateDefaultContent: JSON.stringify(templateDefaultContent),
         source,
         saveType,
      });
   }

   deleteFile(fileName): Observable<any> {
      return this.httpClient.request("delete", "/servlets/templates/deletefile/", { body: { fileName } });
   }

   escapeXmlChars(html): string {
      let escapeHtml = html;
      while (escapeHtml.indexOf("&lt;") !== -1 && escapeHtml.indexOf("&gt;") !== -1) {
         escapeHtml = escapeHtml.replace("&lt;", "<").replace("&gt;", ">");
      }

      return escapeHtml;
   }

   setMedia(data: any, templateId): Observable<any> {
      return this.httpClient.put("/servlets/templates/saveMedia/", {
         data,
         templateId,
      });
   }

   private cleanTemplateDefaultContent(response: any) {
      const { templateDefaultContent } = response;
      if (!Boolean(templateDefaultContent)) {
         return response;
      }
      const cleanModel = Object.keys(templateDefaultContent).reduce((modelNew, key) => {
         const cleanKey = key.replace(/template./g, "");
         return { ...modelNew, [cleanKey]: templateDefaultContent[key] };
      }, {});
      response.templateDefaultContent = cleanModel;
      return response;
   }

   isTemplateUsed(
      templateId: number,
      userId: number,
      communicationsCategoryId: string,
      updatedModel: any,
      shouldOnlyUpdateFileRef: any,
      mediaDevices: any,
      isModelUpdated: boolean
   ): Observable<any> {
      const templateUrl = "/servlets/templates/templateState/";

      return this.httpClient.post(templateUrl, { userId, templateId, communicationsCategoryId }).pipe(
         map((response: any) => {
            const templateState = { ...response.templateState };
            templateState.updatedModel = updatedModel;
            templateState.canUpdate = true;
            templateState.shouldOnlyUpdateFileRef = shouldOnlyUpdateFileRef;
            templateState.mediaDevices = mediaDevices;
            templateState.isModelUpdated = isModelUpdated;
            templateState.userId = userId;
            return templateState;
         })
      );
   }

   convertHtmlToXhtml(htmlText: string): Observable<any> {
      return this.httpClient
         .post("/servlets/templates/autocorrect/", {
            htmlText,
         })
         .pipe(map((response: any) => response.htmlText));
   }
}
